import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bell, BellRing, Plus, Search, Tag, MessageSquarePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StockBadge, Pill } from "@/components/Badges";
import { useCart } from "@/context/CartContext";
import { api, errMsg, money, groupProducts, CATEGORY_LABELS } from "@/lib/api";

const FILTERS = [
  { k: "all", label: "All" },
  { k: "sale", label: "On Sale" },
  { k: "peptide", label: "Peptides" },
  { k: "bac_water", label: "BAC Water" },
  { k: "supply", label: "Supplies" },
];

function ContentPicker({ items, sel, onPick }) {
  if (items.length === 1) return <p className="font-mono-data text-sm text-muted-foreground">{items[0].vial_size}</p>;
  return (
    <div className="mt-3">
      <p className="eyebrow mb-2">Content</p>
      <div className="flex flex-wrap gap-2">
        {items.map((v) => (
          <button key={v.id} type="button" onClick={() => onPick(v.id)} data-testid={`product-content-option-${v.id}`}
            className={`rounded-md border px-3 py-1.5 font-mono-data text-sm transition-colors ${sel === v.id ? "border-emerald-500 bg-emerald-500/10 text-emerald-300" : "border-border text-muted-foreground hover:text-foreground"} ${v.stock_status === "out_of_stock" ? "opacity-60" : ""}`}>
            {v.vial_size}{v.stock_status === "out_of_stock" && <span className="ml-1 text-[10px] uppercase">sold out</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

function NotifyButton({ p, waiting, onNotify }) {
  return (
    <Button size="sm" variant={waiting ? "secondary" : "outline"} onClick={() => onNotify(p, !waiting)} data-testid={`product-notify-button-${p.id}`} className="gap-1.5">
      {waiting ? <><BellRing className="h-4 w-4 text-sky-400" />We'll email you</> : <><Bell className="h-4 w-4" />Notify me</>}
    </Button>
  );
}

function ProductCard({ group, onAdd, alerts, onNotify, i, subName }) {
  const items = group.items;
  const [sel, setSel] = useState(() => (items.find((v) => v.stock_status !== "out_of_stock") || items[0]).id);
  const p = items.find((v) => v.id === sel) || items[0];
  const out = p.stock_status === "out_of_stock";
  return (
    <div className="card-lift flex flex-col rounded-lg border border-border bg-card p-6 rise" style={{ animationDelay: `${i * 40}ms` }} data-testid={`product-card-${group.id}`}>
      <div className="flex items-start justify-between gap-3">
        <span className="eyebrow">{CATEGORY_LABELS[p.category]}</span>
        <div className="flex flex-wrap justify-end gap-1.5">
          {p.on_sale && <Pill color="orange" testId={`product-sale-badge-${p.id}`}><Tag className="h-3 w-3" />Sale</Pill>}
          <StockBadge status={p.stock_status} testId={`product-stock-badge-${p.id}`} />
        </div>
      </div>
      <h3 className="mt-4 text-xl font-semibold">{p.name}</h3>
      {(p.subcategory_ids || []).some((id) => subName[id]) && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`product-subcats-${group.id}`}>
          {p.subcategory_ids.filter((id) => subName[id]).map((id) => <span key={id} className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground">{subName[id]}</span>)}
        </div>
      )}
      <ContentPicker items={items} sel={p.id} onPick={setSel} />
      {p.description && <p className="mt-3 line-clamp-3 text-sm text-muted-foreground">{p.description}</p>}
      {p.category === "peptide" && p.recommended_bac_ml > 0 && (
        <p className="mt-3 font-mono-data text-xs text-sky-400">Recommended BAC water: {p.recommended_bac_ml} mL</p>
      )}
      <div className="mt-auto flex items-end justify-between pt-6">
        <div>
          {p.on_sale ? (
            <div className="flex items-baseline gap-2">
              <span className="font-mono-data text-2xl text-orange-400" data-testid={`product-price-${p.id}`}>{money(p.effective_price)}</span>
              <span className="font-mono-data text-sm text-muted-foreground line-through">{money(p.price)}</span>
            </div>
          ) : (
            <span className="font-mono-data text-2xl" data-testid={`product-price-${p.id}`}>{money(p.price)}</span>
          )}
        </div>
        {out ? <NotifyButton p={p} waiting={alerts.has(p.id)} onNotify={onNotify} /> : (
          <Button size="sm" onClick={() => onAdd(p)} data-testid={`product-add-button-${p.id}`} className="gap-1.5">
            <Plus className="h-4 w-4" /> Add
          </Button>
        )}
      </div>
    </div>
  );
}

export default function Shop() {
  const { add } = useCart();
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [alerts, setAlerts] = useState(new Set());
  const [subcats, setSubcats] = useState([]);
  const [sub, setSub] = useState("all");

  useEffect(() => {
    api.get("/shop/products").then((r) => setProducts(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/my/stock-alerts").then((r) => setAlerts(new Set(r.data))).catch(() => {});
    api.get("/shop/subcategories").then((r) => setSubcats(r.data)).catch(() => {});
  }, []);

  const subName = useMemo(() => Object.fromEntries(subcats.map((s) => [s.id, s.name])), [subcats]);
  const usedSubs = useMemo(() => subcats.filter((s) => products.some((p) => (p.subcategory_ids || []).includes(s.id))), [subcats, products]);
  const showSubs = usedSubs.length > 0 && ["all", "peptide"].includes(filter);

  const shown = useMemo(() => groupProducts(products).filter(({ items }) => {
    const p = items[0];
    if (filter === "sale" && !items.some((v) => v.on_sale)) return false;
    if (!["all", "sale"].includes(filter) && p.category !== filter) return false;
    if (showSubs && sub !== "all" && !(p.subcategory_ids || []).includes(sub)) return false;
    return p.name.toLowerCase().includes(q.toLowerCase());
  }), [products, filter, q, sub, showSubs]);

  const onAdd = (p) => {
    add(p);
    toast.success(`${p.name} ${p.vial_size || ""} added to cart`);
  };

  const onNotify = async (p, on) => {
    try {
      if (on) await api.post("/my/stock-alerts", { product_id: p.id });
      else await api.delete(`/my/stock-alerts/${p.id}`);
      setAlerts((s) => { const n = new Set(s); on ? n.add(p.id) : n.delete(p.id); return n; });
      toast.success(on ? `We'll email you when ${p.name} ${p.vial_size || ""} is back` : "Alert removed");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:py-14">
      <div className="mb-10 rise">
        <p className="eyebrow mb-3">Catalog</p>
        <h1 className="text-4xl font-bold sm:text-5xl">Shop Korevex Bio</h1>
        <p className="mt-3 max-w-xl text-muted-foreground">Add vials to your cart. You can choose reconstitution and BAC water amount for each peptide at checkout.</p>
      </div>
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`shop-filter-${f.k}`}
              className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${filter === f.k ? "border-emerald-500 bg-emerald-500/10 text-emerald-300" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative md:w-72">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search products" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="shop-search-input" />
        </div>
      </div>
      {showSubs && (
        <div className="-mt-4 mb-8 flex flex-wrap items-center gap-2" data-testid="shop-subcategory-filters">
          <span className="eyebrow mr-1">Peptide type</span>
          {[{ id: "all", name: "All" }, ...usedSubs].map((s) => (
            <button key={s.id} onClick={() => setSub(s.id)} data-testid={`shop-subfilter-${s.id}`}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${sub === s.id ? "border-sky-400 bg-sky-400/10 text-sky-300" : "border-border text-muted-foreground hover:text-foreground"}`}>{s.name}</button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="product-grid">
        {shown.map((g, i) => <ProductCard key={g.id} group={g} i={i} onAdd={onAdd} alerts={alerts} onNotify={onNotify} subName={subName} />)}
      </div>
      {shown.length === 0 && <p className="py-16 text-center text-muted-foreground" data-testid="shop-empty">No products match.</p>}
      <div className="mt-14 flex flex-col items-start justify-between gap-4 rounded-lg border border-dashed border-emerald-500/40 bg-emerald-500/5 p-8 sm:flex-row sm:items-center">
        <div>
          <h3 className="text-xl font-semibold">Don't see the peptide you need?</h3>
          <p className="mt-1 text-sm text-muted-foreground">Send a request and Korevex Bio will look into sourcing it.</p>
        </div>
        <Button asChild variant="outline" className="gap-2" data-testid="shop-request-peptide-link">
          <Link to="/request"><MessageSquarePlus className="h-4 w-4" /> Request a peptide</Link>
        </Button>
      </div>
    </div>
  );
}
